import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash-es';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  STATE__ANONYMOUS,
  STATE__AUTHORIZED,
} from '../../../constants';
import {
  setSelectedMenuItem,
  setSelectedElement,
} from '../../actions';
import {
  MENU_ITEM__QUESTIONNAIRES__AVAILABLE,
  MENU_ITEM__FORMS__ASSIGNED,
  MENU_ITEM__FORMS__DRAFT,
  MENU_ITEM__FORMS__RETURNED,
  MENU_ITEM__FORMS__SENT,
  MENU_ITEM__FORMS__APPROVED,
} from '../../constants';

import Loader from '../../../shared/components/loader';

import ApprovedButtonBlueImage from '../../../assets/icons/approved-blue.svg';
import ApprovedButtonGrayImage from '../../../assets/icons/approved-gray.svg';
import ApprovedButtonNavyImage from '../../../assets/icons/approved-navy.svg';
import AssignedButtonBlueImage from '../../../assets/icons/assigned-blue.svg';
import AssignedButtonGrayImage from '../../../assets/icons/assigned-gray.svg';
import AssignedButtonNavyImage from '../../../assets/icons/assigned-navy.svg';
import AvailableButtonBlueImage from '../../../assets/icons/available-blue.svg';
import AvailableButtonGrayImage from '../../../assets/icons/available-gray.svg';
import AvailableButtonNavyImage from '../../../assets/icons/available-navy.svg';
import DraftButtonBlueImage from '../../../assets/icons/draft-blue.svg';
import DraftButtonGrayImage from '../../../assets/icons/draft-gray.svg';
import DraftButtonNavyImage from '../../../assets/icons/draft-navy.svg';
import ReturnedButtonBlueImage from '../../../assets/icons/returned-blue.svg';
import ReturnedButtonGrayImage from '../../../assets/icons/returned-gray.svg';
import ReturnedButtonNavyImage from '../../../assets/icons/returned-navy.svg';
import SentButtonBlueImage from '../../../assets/icons/sent-blue.svg';
import SentButtonGrayImage from '../../../assets/icons/sent-gray.svg';
import SentButtonNavyImage from '../../../assets/icons/sent-navy.svg';

const Menu = (props) => {
  const dispatch = useDispatch();
  const state = useSelector((store) => store.application.get('state'));
  const selectedMenuItem = useSelector((store) => store.authorizedState.get('selectedMenuItem'));
  const selectedElement = useSelector((store) => store.authorizedState.get('selectedElement'));

  useEffect(() => {
    if (state !== STATE__ANONYMOUS) {
      props.triggerQuestionnaireCount();
    }
  }, []);

  const changeSelectedMenuItem = (newMenuItem) => {
    if (!selectedElement || [MENU_ITEM__FORMS__SENT, MENU_ITEM__FORMS__APPROVED].includes(selectedMenuItem)) {
      props.triggerQuestionnaireCount();
      dispatch(setSelectedElement(null));
      dispatch(setSelectedMenuItem(newMenuItem));
      if (newMenuItem === selectedMenuItem) {
        props.refreshMainContent();
      }
    } else {
      props.changeSelectedMenuItem(newMenuItem);
    }
  };

  const availableQuestionnairesButtonClick = () => {
    changeSelectedMenuItem(MENU_ITEM__QUESTIONNAIRES__AVAILABLE);
  };

  const assignedFormsButtonClick = () => {
    changeSelectedMenuItem(MENU_ITEM__FORMS__ASSIGNED);
  };

  const draftFormsButtonClick = () => {
    changeSelectedMenuItem(MENU_ITEM__FORMS__DRAFT);
  };

  const returnedFormsButtonClick = () => {
    changeSelectedMenuItem(MENU_ITEM__FORMS__RETURNED);
  };

  const sentFormsButtonClick = () => {
    changeSelectedMenuItem(MENU_ITEM__FORMS__SENT);
  };

  const approvedFormsButtonClick = () => {
    changeSelectedMenuItem(MENU_ITEM__FORMS__APPROVED);
  };

  const getAvailableQuestionnairesCount = (questionnaireItems) => {
    const sentQuestionnaires = localStorage.getItem('questionnaires:sent');
    const sentQuestionnairesList = sentQuestionnaires ? JSON.parse(sentQuestionnaires) : [];

    return questionnaireItems.filter((item) => !sentQuestionnairesList.includes(item.id)).length;
  };

  const [isAvailableQuestionnairesButtonHover, setIsAvailableQuestionnairesButtonHover] = useState(false);
  const [isAssignedFormsButtonHover, setIsAssignedFormsButtonHover] = useState(false);
  const [isDraftFormsButtonHover, setIsDraftFormsButtonHover] = useState(false);
  const [isReturnedFormsButtonHover, setIsReturnedFormsButtonHover] = useState(false);
  const [isSentFormsButtonHover, setIsSentFormsButtonHover] = useState(false);
  const [isApprovedFormsButtonHover, setIsApprovedFormsButtonHover] = useState(false);

  return (
    <div className="menu">
      <div className="section">
        <If condition={state === STATE__ANONYMOUS}>
          <div className="section-header">
            <strong>Twoje </strong>
            ankiety
          </div>
          <div className="section-content">
            <Choose>
              <When condition={props.questionnairesFetching}>
                <Loader />
              </When>
              <Otherwise>
                <div
                  className={`menu-item ${selectedMenuItem === MENU_ITEM__QUESTIONNAIRES__AVAILABLE ? 'selected' : ''}`}
                  onMouseEnter={() => setIsAvailableQuestionnairesButtonHover(true)}
                  onMouseLeave={() => setIsAvailableQuestionnairesButtonHover(false)}
                  onClick={availableQuestionnairesButtonClick}
                  role="button"
                  title="Dostępne"
                >
                  <Choose>
                    <When condition={selectedMenuItem === MENU_ITEM__QUESTIONNAIRES__AVAILABLE}>
                      <img className="icon" src={AvailableButtonNavyImage} alt="Przydzielone" />
                    </When>
                    <When condition={isAvailableQuestionnairesButtonHover}>
                      <img className="icon" src={AvailableButtonBlueImage} alt="Przydzielone" />
                    </When>
                    <Otherwise>
                      <img className="icon" src={AvailableButtonGrayImage} alt="Przydzielone" />
                    </Otherwise>
                  </Choose>
                  Dostępne
                  {!isNil(props.questionnairesData) && ` (${getAvailableQuestionnairesCount(props.questionnairesData)})`}
                </div>
              </Otherwise>
            </Choose>
          </div>
        </If>
        <If condition={state === STATE__AUTHORIZED}>
          <div className="section-header">
            <strong>Twoje </strong>
            formularze
          </div>
          <div className="section-content">
            <Choose>
              <When condition={props.countersFetching}>
                <Loader />
              </When>
              <Otherwise>
                <div
                  className={`menu-item ${selectedMenuItem === MENU_ITEM__FORMS__ASSIGNED ? 'selected' : ''}`}
                  onMouseEnter={() => setIsAssignedFormsButtonHover(true)}
                  onMouseLeave={() => setIsAssignedFormsButtonHover(false)}
                  onClick={assignedFormsButtonClick}
                  role="button"
                  title="Przydzielone"
                >
                  <Choose>
                    <When condition={selectedMenuItem === MENU_ITEM__FORMS__ASSIGNED}>
                      <img className="icon" src={AssignedButtonNavyImage} alt="Przydzielone" />
                    </When>
                    <When condition={isAssignedFormsButtonHover}>
                      <img className="icon" src={AssignedButtonBlueImage} alt="Przydzielone" />
                    </When>
                    <Otherwise>
                      <img className="icon" src={AssignedButtonGrayImage} alt="Przydzielone" />
                    </Otherwise>
                  </Choose>
                  Przydzielone
                  {props.assignedQuestionnaireCount > 0 ? ` (${props.assignedQuestionnaireCount})` : ''}
                </div>
                <div
                  className={`menu-item ${selectedMenuItem === MENU_ITEM__FORMS__RETURNED ? 'selected' : ''}`}
                  onMouseEnter={() => setIsReturnedFormsButtonHover(true)}
                  onMouseLeave={() => setIsReturnedFormsButtonHover(false)}
                  onClick={returnedFormsButtonClick}
                  role="button"
                  title="Zwrócone"
                >
                  <Choose>
                    <When condition={selectedMenuItem === MENU_ITEM__FORMS__RETURNED}>
                      <img className="icon" src={ReturnedButtonNavyImage} alt="Zwrócone" />
                    </When>
                    <When condition={isReturnedFormsButtonHover}>
                      <img className="icon" src={ReturnedButtonBlueImage} alt="Zwrócone" />
                    </When>
                    <Otherwise>
                      <img className="icon" src={ReturnedButtonGrayImage} alt="Zwrócone" />
                    </Otherwise>
                  </Choose>
                  Zwrócone
                  {props.returnedQuestionnaireCount > 0 ? ` (${props.returnedQuestionnaireCount})` : ''}
                </div>
                <div
                  className={`menu-item ${selectedMenuItem === MENU_ITEM__FORMS__DRAFT ? 'selected' : ''}`}
                  onMouseEnter={() => setIsDraftFormsButtonHover(true)}
                  onMouseLeave={() => setIsDraftFormsButtonHover(false)}
                  onClick={draftFormsButtonClick}
                  role="button"
                  title="Robocze"
                >
                  <Choose>
                    <When condition={selectedMenuItem === MENU_ITEM__FORMS__DRAFT}>
                      <img className="icon" src={DraftButtonNavyImage} alt="Robocze" />
                    </When>
                    <When condition={isDraftFormsButtonHover}>
                      <img className="icon" src={DraftButtonBlueImage} alt="Robocze" />
                    </When>
                    <Otherwise>
                      <img className="icon" src={DraftButtonGrayImage} alt="Robocze" />
                    </Otherwise>
                  </Choose>
                  Robocze
                  {props.draftQuestionnaireCount > 0 ? ` (${props.draftQuestionnaireCount})` : ''}
                </div>
                <div
                  className={`menu-item ${selectedMenuItem === MENU_ITEM__FORMS__SENT ? 'selected' : ''}`}
                  onMouseEnter={() => setIsSentFormsButtonHover(true)}
                  onMouseLeave={() => setIsSentFormsButtonHover(false)}
                  onClick={sentFormsButtonClick}
                  role="button"
                  title="Wysłane"
                >
                  <Choose>
                    <When condition={selectedMenuItem === MENU_ITEM__FORMS__SENT}>
                      <img className="icon" src={SentButtonNavyImage} alt="Wysłane" />
                    </When>
                    <When condition={isSentFormsButtonHover}>
                      <img className="icon" src={SentButtonBlueImage} alt="Wysłane" />
                    </When>
                    <Otherwise>
                      <img className="icon" src={SentButtonGrayImage} alt="Wysłane" />
                    </Otherwise>
                  </Choose>
                  Wysłane
                  {props.sentQuestionnaireCount > 0 ? ` (${props.sentQuestionnaireCount})` : ''}
                </div>
                <div
                  className={`menu-item ${selectedMenuItem === MENU_ITEM__FORMS__APPROVED ? 'selected' : ''}`}
                  onMouseEnter={() => setIsApprovedFormsButtonHover(true)}
                  onMouseLeave={() => setIsApprovedFormsButtonHover(false)}
                  onClick={approvedFormsButtonClick}
                  role="button"
                  title="Zatwierdzone"
                >
                  <Choose>
                    <When condition={selectedMenuItem === MENU_ITEM__FORMS__APPROVED}>
                      <img className="icon" src={ApprovedButtonNavyImage} alt="Zatwierdzone" />
                    </When>
                    <When condition={isApprovedFormsButtonHover}>
                      <img className="icon" src={ApprovedButtonBlueImage} alt="Zatwierdzone" />
                    </When>
                    <Otherwise>
                      <img className="icon" src={ApprovedButtonGrayImage} alt="Zatwierdzone" />
                    </Otherwise>
                  </Choose>
                  Zatwierdzone
                  {props.approvedQuestionnaireCount > 0 ? ` (${props.approvedQuestionnaireCount})` : ''}
                </div>
              </Otherwise>
            </Choose>
          </div>
        </If>
      </div>
    </div>
  );
};

Menu.propTypes = {
  questionnairesData: PropTypes.oneOfType([
    PropTypes.shape(),
    PropTypes.arrayOf(PropTypes.shape()),
  ]),
  assignedQuestionnaireCount: PropTypes.number,
  returnedQuestionnaireCount: PropTypes.number,
  sentQuestionnaireCount: PropTypes.number,
  draftQuestionnaireCount: PropTypes.number,
  approvedQuestionnaireCount: PropTypes.number,
  triggerQuestionnaireCount: PropTypes.func.isRequired,
  countersFetching: PropTypes.bool,
  questionnairesFetching: PropTypes.bool,
  changeSelectedMenuItem: PropTypes.func,
  refreshMainContent: PropTypes.func,
};

Menu.defaultProps = {
  assignedQuestionnaireCount: null,
  returnedQuestionnaireCount: null,
  sentQuestionnaireCount: null,
  draftQuestionnaireCount: null,
  approvedQuestionnaireCount: null,
  questionnairesData: null,
  countersFetching: false,
  questionnairesFetching: false,
  changeSelectedMenuItem: () => {},
  refreshMainContent: () => {},
};

export default Menu;
